import { PageHero } from "@/components/PageHero";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal, SectionHeading } from "@/components/motion";
import { CtaBand } from "@/components/CtaBand";
import { usePageMeta } from "@/lib/seo";


export const WhyUniversHeal = () => {
    const {  t, tHtml, site: siteData } = useLang();
    const { DIFFERENCE_CARDS } = siteData;
    const WE_ARE = [
        t("whyu.weare1", "A healthcare coordination company for international patients"),
        t("whyu.weare2", "Your single, dedicated point of contact"),
        t("whyu.weare3", "Help understanding consultations, options and planning"),
        t("whyu.weare4", "Support before travel, during treatment and after returning home"),
    ];
    const WE_ARE_NOT = [
        t("whyu.wearenot1", "A hospital or a clinic"),
        t("whyu.wearenot2", "A provider of medical treatment or diagnosis"),
        t("whyu.wearenot3", "A substitute for professional medical advice"),
        t("whyu.wearenot4", "A promise of any particular clinical outcome"),
    ];
    const VALUES = [
        {
            title: t("whyu.value1.title", "Trust"),
            desc: t("whyu.value1.desc", "We coordinate only with providers we would trust for our own families."),
        },
        {
            title: t("whyu.value2.title", "Clarity"),
            desc: t("whyu.value2.desc", "Clear information at every step, in plain language, with no surprises."),
        },
        {
            title: t("whyu.value3.title", "Warmth"),
            desc: t("whyu.value3.desc", "Care that treats you as a person, never as a case number."),
        },
        {
            title: t("whyu.value4.title", "Accountability"),
            desc: t("whyu.value4.desc", "One responsible contact, answerable to you throughout your journey."),
        },
    ];
    usePageMeta(
        t("meta.why_univers_heal.title", "Why UniversHeal"),
        t("meta.why_univers_heal.desc", "UniversHeal is a healthcare coordination partner for international patients — personalized coordination, clear information and human support throughout your journey in India.")
    );
    return (
        <>
            <PageHero
                eyebrow={t("why_universheal.eyebrow", "Why UniversHeal")}
                title={
                    <>
                        More than treatment. A{" "}
                        <span className="italic text-pine">partner</span> in your
                        {t("why_univers_heal.text1", "healthcare journey.")}
                    </>
                }
                intro={t("why_universheal.intro", "Navigating healthcare far from home can feel overwhelming — unfamiliar systems, new faces, important decisions. UniversHeal exists to make it feel close, clear and personally guided.")}
            />

            <section className="section-pad pt-4">
                <div className="container-site">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {DIFFERENCE_CARDS.map((card, i) => (
                            <Reveal key={card.title} delay={i * 0.07} className="h-full">
                                <article
                                    data-testid={`why-healease-pillar-${i + 1}`}
                                    className="h-full bg-white border border-pine/10 rounded-2xl p-8 card-lift"
                                >
                                    <span className="font-serif italic text-lg text-natural">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <h3 className="mt-6 font-serif font-medium text-2xl leading-snug text-forest">
                                        {card.title}
                                    </h3>
                                    <p className="mt-3 text-sm leading-relaxed text-moss">
                                        {card.desc}
                                    </p>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section-pad pt-0">
                <div className="container-site">
                    <SectionHeading
                        index="—"
                        eyebrow={t("why_universheal.eyebrow", "Transparency")}
                        title={t("why_univers_heal.title1", "What we are — and what we are not.")}
                        intro={t("why_universheal.intro", "Because honest expectations are the foundation of trust.")}
                    />
                    <div className="mt-12 grid md:grid-cols-2 gap-5">
                        <Reveal className="h-full">
                            <div
                                data-testid="we-are-card"
                                className="h-full bg-forest text-ivory rounded-2xl p-8 sm:p-10"
                            >
                                <p className="eyebrow text-natural">{t("whyu.isLabel", "UniversHeal is")}</p>
                                <ul className="mt-6 space-y-4">
                                    {WE_ARE.map((item) => (
                                        <li
                                            key={item}
                                            className="flex items-start gap-3 text-[15px] leading-relaxed"
                                        >
                                            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-natural shrink-0" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                        <Reveal delay={0.1} className="h-full">
                            <div
                                data-testid="we-are-not-card"
                                className="h-full bg-white border border-pine/15 rounded-2xl p-8 sm:p-10"
                            >
                                <p className="eyebrow text-moss">{t("whyu.isNotLabel", "UniversHeal is not")}</p>
                                <ul className="mt-6 space-y-4">
                                    {WE_ARE_NOT.map((item) => (
                                        <li
                                            key={item}
                                            className="flex items-start gap-3 text-[15px] leading-relaxed text-charcoal/80"
                                        >
                                            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-moss/50 shrink-0" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                    </div>

                    <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
                        {VALUES.map((v, i) => (
                            <Reveal key={v.title} delay={i * 0.06}>
                                <div data-testid={`value-${i + 1}`}>
                                    <h3 className="font-serif italic text-2xl text-pine">
                                        {v.title}
                                    </h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-moss">
                                        {v.desc}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <CtaBand />
        </>
    );
};
